/**
 * Top-level React error boundary. A single unhandled render-time exception
 * would otherwise white-screen the whole SPA — this contains it and offers a
 * reload instead (audit ARCH-4). Class component because error boundaries
 * have no hook equivalent; no external dependency.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surface for debugging; a telemetry sink can attach here later.
    console.error('Unhandled render error:', error, info.componentStack);
  }

  override render(): ReactNode {
    if (this.state.error) {
      if (this.props.fallback !== undefined) return this.props.fallback;
      return (
        <div className="flex min-h-[50vh] flex-col items-center justify-center gap-3 p-8 text-center">
          <p className="text-sm font-semibold text-ink">Something went wrong</p>
          <p className="max-w-md text-xs text-ink-muted">
            An unexpected error occurred while rendering this page. Reloading
            usually fixes it.
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-tight border border-rule px-3 py-1.5 text-xs text-ink transition-colors hover:bg-paper-sunk"
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
